import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from "react";
import classes from "./UpgradeAccount.module.css"
import styles from "./Modal.module.css";
import AppHeader from "../Components/AppHeader";
import AppFooter from '../Components/AppFooter';
import RadioInput from "../Components/RadioInput";
import Toast from "../Components/Toast";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import { showSignedOutToast } from '../utils/sessionToast';
import { storedStringValue } from '../utils/localStorageString';
import {
    useDictionaryRecurringCancel,
    isDictionaryRecurringActive,
    isDictionaryRecurringCancelledState,
    getRecurringCancelledStatusLines,
} from '../Hooks/useDictionaryRecurringCancel';
import { fetchStrapiGetJsonCached } from '../utils/strapiCachedFetch';

function accountTierFromStorage() {
    const acc = storedStringValue(window.localStorage.getItem("account"));
    return acc === "2" ? "book" : "basic";
}

/** Misma clave que en UpgradeBookAccount.js (código vinculado en este dispositivo). */
const LINKED_BOOK_CODE_KEY = "linkedBookCode";

function readLinkedBookCodeFromStorage() {
    const raw = window.localStorage.getItem(LINKED_BOOK_CODE_KEY);
    const trimmed = raw != null ? String(raw).trim() : "";
    return trimmed;
}

/** Tarjeta “current subscription” — EN como en diseño; DE traducción. */
const BASIC_CURRENT_CARD = {
    de: {
        ribbon: "AKTUELLES ABONNEMENT",
        account: "Basis-Account",
        features: [
            "Suche auf Deutsch, Französisch, Italienisch und Englisch",
            "Übersetzungen auf Französisch, Italienisch und Englisch anzeigen",
            "Begriff kopieren",
            "Begriff teilen",
            "Keine Werbung",
        ],
    },
    en: {
        ribbon: "CURRENT SUBSCRIPTION",
        account: "Basic account",
        features: [
            "Search in German, French, Italian and English",
            "See the translation in French, Italian and English",
            "Copy term",
            "Share term",
            "No ads",
        ],
    },
};

/** Lista Buch / Premium — EN como en diseño; DE traducción. */
const BOOK_PREMIUM_FEATURES = {
    de: [
        "Suche auf Deutsch, Französisch, Italienisch und Englisch",
        "Übersetzungen auf Französisch, Italienisch und Englisch anzeigen",
        "Beschreibung jedes Begriffs auf Deutsch verfügbar",
        "Begriff kopieren",
        "Begriff teilen",
        "Interne Links verfügbar (einfache Navigation zu referenzierten Begriffen)",
        "Externe Links verfügbar (Sprung zum rechtlichen Kontext eines Begriffs)",
        "Keine Werbung",
        "Zugangscode im Buch",
    ],
    en: [
        "Search in German, French, Italian and English",
        "See the translation in French, Italian and English",
        "Description of each term available in German",
        "Copy term",
        "Share term",
        "Internal links available (easy navigation to referenced terms)",
        "External links available (jump to legal context of a term)",
        "No ads",
        "Access code inside the book",
    ],
};

const UPGRADE_INTRO_PARAGRAPHS = {
    de: [
        "Wenn Sie das Buch bereits haben, können Sie upgraden, indem Sie den Code in Ihrem Buch verknüpfen.",
        "Wenn Sie das Buch noch nicht haben, können Sie ein Abonnement abschließen und Premium-Zugang erhalten.",
        "Sie können monatlich und jährlich abonnieren.",
    ],
    en: [
        "If you already have the book, you can upgrade by linking the code that is in your book.",
        "But if you don't have the book, you can subscribe and have premium access.",
        "You can subscribe monthly and annually.",
    ],
};

/** Copia fija (el fetch a Strapi sigue ejecutándose). */
const UPGRADE_ACCOUNT_UI_COPY = {
    de: {
        title: "Konto verwalten",
        change_password_link: "Passwort ändern",
        change_your_subscription: "Abonnement wechseln",
        book_premium_heading: "Buch-Account & Premium-Account",
        book_account_name: "Buch-Account",
        premium_account_name: "Premium-Account",
        linked_book_code_label: "Verknüpfter Buchcode",
        unlink_book_code: "Buchcode entfernen",
        unlink_book_code_loading: "Lädt…",
        book_code_unlink_failed: "Der Buchcode konnte nicht entfernt werden.",
        book_code_unlinked_toast: "Der Buchcode wurde von Ihrem Konto getrennt.",
        session_expired_toast: "Sitzung abgelaufen. Bitte melden Sie sich erneut an.",
        delete_account_link: "Konto löschen",
        delete_account_validation_text: "Bitte wählen Sie einen Grund.",
        modal_delete_account_text: "Konto löschen",
        modal_delete_account_question: "Warum möchten Sie Ihr Konto löschen?",
        modal_delete_account_option_1: "Ich nutze die App nicht",
        modal_delete_account_option_2: "Sie senden zu viele E-Mails",
        modal_delete_account_option_3: "Die App ist für mich nicht nützlich",
        modal_delete_account_option_4: "Etwas anderes",
        modal_delete_account_confirm: "Löschen",
        modal_delete_account_cancel: "Abbrechen",
        modal_change_password_text: "Passwort ändern",
        modal_change_password_confirm: "Weiter",
        modal_change_password_cancel: "Abbrechen",
        upgrade_account_button: "Upgrade",
        back_to_profile: "Zurück zum Profil",
    },
    en: {
        title: "Manage your account",
        change_password_link: "Change password",
        change_your_subscription: "Change your subscription",
        book_premium_heading: "Book account & Premium account",
        book_account_name: "Book Account",
        premium_account_name: "Premium account",
        linked_book_code_label: "Linked book code",
        unlink_book_code: "Unlink book code",
        unlink_book_code_loading: "Loading…",
        book_code_unlink_failed: "Could not unlink the book code.",
        book_code_unlinked_toast: "The book code has been unlinked from your account.",
        session_expired_toast: "Session expired. Please sign in again.",
        delete_account_link: "Delete account",
        delete_account_validation_text: "Please select a reason.",
        modal_delete_account_text: "Delete account",
        modal_delete_account_question: "Why do you want to delete your account?",
        modal_delete_account_option_1: "I don't use the app",
        modal_delete_account_option_2: "You send too many emails",
        modal_delete_account_option_3: "The app is not useful to me",
        modal_delete_account_option_4: "Something else",
        modal_delete_account_confirm: "Delete",
        modal_delete_account_cancel: "Cancel",
        modal_change_password_text: "Change password",
        modal_change_password_confirm: "Continue",
        modal_change_password_cancel: "Cancel",
        upgrade_account_button: "Upgrade",
        back_to_profile: "Back to profile",
    },
};

function featureLiPlain(text, idx) {
    return <li key={`plain-${idx}`}>{text}</li>;
}

export default function UpgradeAccount(props) {
    const MODAL_CLOSE_ANIMATION_MS = 180;
    const [subscription, setSubscription] = useState(() => accountTierFromStorage())
    const [linkedBookCode, setLinkedBookCode] = useState(() => readLinkedBookCodeFromStorage())
    const [mobile, setMobile] = useState(false)
    const [comeFrom, setComeFrom] = useState(window.localStorage.getItem("comeFrom"))
    const [, setPageContent] = useState({})
    const [isOpenModalChangePass, setIsOpenModalChangePass] = useState(false);
    const [isOpenModalDeleteAccount, setIsOpenModalDeleteAccount] = useState(false);
    const [isOpenModalCancelRecurring, setIsOpenModalCancelRecurring] = useState(false);
    const [isOpenModalReactivateRecurring, setIsOpenModalReactivateRecurring] = useState(false);
    const [isClosingModalChangePass, setIsClosingModalChangePass] = useState(false);
    const [isClosingModalDeleteAccount, setIsClosingModalDeleteAccount] = useState(false);
    const [isClosingModalCancelRecurring, setIsClosingModalCancelRecurring] = useState(false);
    const [isClosingModalReactivateRecurring, setIsClosingModalReactivateRecurring] = useState(false);

    const initError = { exists: false, helperText: null };
    const [deleteReasonError, setDeleteReasonError] = useState(initError);
    const [selected, setSelected] = useState("");
    const [releasingBookCode, setReleasingBookCode] = useState(false);
    const [toastVisible, setToastVisible] = useState(false);
    const [toastProperties, setToastProperties] = useState({});
    const [toastPosition, setToastPosition] = useState("top-right");
    const shellRef = useRef(null);
    const [backLinkLayout, setBackLinkLayout] = useState(null);
    const modalCloseTimeoutsRef = useRef([]);

    const recurring = useDictionaryRecurringCancel({
        language: props.language,
        history: props.history,
    });

    const closeModalWithAnimation = useCallback((setClosing, setOpen, onAfterClose) => {
        setClosing(true);
        const timerId = window.setTimeout(() => {
            setOpen(false);
            setClosing(false);
            if (typeof onAfterClose === "function") onAfterClose();
        }, MODAL_CLOSE_ANIMATION_MS);
        modalCloseTimeoutsRef.current.push(timerId);
    }, []);

    useEffect(() => {
        return () => {
            modalCloseTimeoutsRef.current.forEach((id) => window.clearTimeout(id));
            modalCloseTimeoutsRef.current = [];
        };
    }, []);

    useEffect(() => {
        const base = process.env.REACT_APP_API_STRAPI_URL;
        const opts = { 'Content-Type': 'application/json', method: 'GET' };
        Promise.all([
            fetchStrapiGetJsonCached(`${base}api/upgrade-account/?locale=${props.language}`, opts),
            fetchStrapiGetJsonCached(`${base}api/profile/?locale=${props.language}`, opts),
        ])
            .then(([upgradeRes, profileRes]) => {
                const upgrade = upgradeRes?.data?.attributes || {};
                const profile = profileRes?.data?.attributes || {};
                /* Perfil aporta textos de modales (eliminar cuenta, etc.); upgrade-account prioriza título y campos propios */
                setPageContent({ ...profile, ...upgrade });
            })
            .catch(() => {});
    }, [props.language])

    useEffect(() => {
        const body = document.getElementById("body");
        if (!body) return undefined;
        if (window.location.pathname.startsWith("/upgradeAccount")) {
            body.style.overflowY = "auto";
        } else {
            body.style.overflowY = "scroll";
        }
        return () => {
            body.style.overflowY = "";
        };
    }, []);

    useEffect(() => {
        window.innerWidth < 900 ? setMobile(true) : setMobile(false)
    }, [])

    useEffect(() => {
        setSubscription(accountTierFromStorage());
        setLinkedBookCode(readLinkedBookCodeFromStorage());
    }, [props.location.pathname]);

    useLayoutEffect(() => {
        const shell = shellRef.current;
        if (!shell || typeof window === "undefined") return undefined;

        const HEADER_LOGO_BOTTOM_GAP = 15;

        const measure = () => {
            const header = document.querySelector("header");
            const brand = document.querySelector("header a.navbar-brand");
            if (!header || !brand) {
                setBackLinkLayout(null);
                return;
            }
            const headerRect = header.getBoundingClientRect();
            const brandRect = brand.getBoundingClientRect();
            const shellRect = shell.getBoundingClientRect();
            const shellCs = window.getComputedStyle(shell);
            const borderLeft = parseFloat(shellCs.borderLeftWidth) || 0;
            const borderTop = parseFloat(shellCs.borderTopWidth) || 0;
            const originLeft = shellRect.left + borderLeft;
            const originTop = shellRect.top + borderTop;
            const left = brandRect.right - originLeft;
            const top = headerRect.bottom + HEADER_LOGO_BOTTOM_GAP - originTop;
            setBackLinkLayout({
                left: Math.round(left * 10) / 10,
                top: Math.max(0, Math.round(top * 10) / 10),
            });
        };

        const raf = requestAnimationFrame(() => measure());
        const onWin = () => measure();
        window.addEventListener("resize", onWin);
        window.addEventListener("orientationchange", onWin);
        window.addEventListener("scroll", onWin, true);
        let ro;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(measure);
            ro.observe(shell);
            const headerEl = document.querySelector("header");
            if (headerEl) ro.observe(headerEl);
        }
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", onWin);
            window.removeEventListener("orientationchange", onWin);
            window.removeEventListener("scroll", onWin, true);
            ro?.disconnect();
        };
    }, [mobile, props.location.pathname]);

    let id = window.localStorage.getItem("id")
    const langKey = props.language === "de" ? "de" : "en";
    const uiCopy = UPGRADE_ACCOUNT_UI_COPY[langKey];
    const bookPremiumFeatures = BOOK_PREMIUM_FEATURES[langKey];
    const upgradeIntroParagraphs = UPGRADE_INTRO_PARAGRAPHS[langKey];
    const premiumPaymentFeatures = bookPremiumFeatures.slice(0, -1);
    const isBookTier = subscription === "book";
    const hasLinkedBookCode = Boolean(linkedBookCode);
    const currentSubscriptionFeatures =
        !isBookTier
            ? BASIC_CURRENT_CARD[langKey].features
            : hasLinkedBookCode
            ? bookPremiumFeatures
            : premiumPaymentFeatures;
    const currentSubscriptionTitle = !isBookTier
        ? BASIC_CURRENT_CARD[langKey].account
        : hasLinkedBookCode
        ? uiCopy.book_account_name
        : uiCopy.premium_account_name;

    const recurringCancelledLines =
        isBookTier &&
        !recurring.authCheckLoading &&
        isDictionaryRecurringCancelledState(recurring.authCheck)
            ? getRecurringCancelledStatusLines(recurring.authCheck, langKey)
            : null;

    const releaseLinkedBookCode = useCallback(() => {
        if (releasingBookCode || !linkedBookCode) return;
        const copy = UPGRADE_ACCOUNT_UI_COPY[langKey];
        const accountInfo = {
            id: storedStringValue(window.localStorage.getItem("id")),
            token: storedStringValue(window.localStorage.getItem("jwt")),
            code: linkedBookCode,
        };
        setReleasingBookCode(true);
        fetch(`${process.env.REACT_APP_API_GOLANG_URL}/v1/releasebookcode/`, {
            method: "POST",
            headers: {
                accept: "application/json",
                "Content-Type": "application/json",
            },
            body: JSON.stringify(accountInfo),
        })
            .then((response) => {
                if (response.status === 403) {
                    setReleasingBookCode(false);
                    setToastPosition("session-top-right");
                    setToastVisible(true);
                    setTimeout(() => setToastVisible(false), 15000);
                    setToastProperties({
                        description: copy.session_expired_toast,
                        borderColor: "#f33757",
                        icon: "icon-warning",
                    });
                    return;
                }
                if (response.status !== 200) {
                    setReleasingBookCode(false);
                    setToastPosition("top-right");
                    setToastVisible(true);
                    setTimeout(() => setToastVisible(false), 8000);
                    setToastProperties({
                        description: copy.book_code_unlink_failed,
                        borderColor: "#f33757",
                        icon: "icon-warning",
                    });
                    return;
                }
                setToastPosition("top-right");
                setToastVisible(true);
                setToastProperties({
                    description: copy.book_code_unlinked_toast,
                    borderColor: "#8DD037",
                    icon: "icon-success",
                });
                setTimeout(() => {
                    const rawId = window.localStorage.getItem("id");
                    fetch(
                        `${process.env.REACT_APP_API_GOLANG_URL}/v1/removeactiveuser/${rawId}`,
                        { method: "GET" }
                    );
                    window.localStorage.removeItem("jwt");
                    window.localStorage.removeItem("id");
                    window.localStorage.removeItem("account");
                    window.localStorage.removeItem("token");
                    window.localStorage.removeItem(LINKED_BOOK_CODE_KEY);
                    setLinkedBookCode("");
                    setReleasingBookCode(false);
                    setToastVisible(false);
                    props.history.push({ pathname: "/login" });
                }, 2500);
            })
            .catch(() => {
                setReleasingBookCode(false);
                setToastPosition("top-right");
                setToastVisible(true);
                setTimeout(() => setToastVisible(false), 8000);
                setToastProperties({
                    description: copy.book_code_unlink_failed,
                    borderColor: "#f33757",
                    icon: "icon-warning",
                });
            });
    }, [
        releasingBookCode,
        linkedBookCode,
        props.history,
        langKey,
    ]);

    const confirmDelete = () => {
        if (selected === "") {
            setDeleteReasonError({
                exists: true,
                helperText: uiCopy.delete_account_validation_text,
            });
            return;
        }
        const userId = window.localStorage.getItem("id");
        const deleteData = {
            id: userId,
            token: window.localStorage.getItem("jwt"),
        };
        const saveReasonData = {
            id: userId,
            reason: selected[0],
        };
        fetch(`${process.env.REACT_APP_API_GOLANG_URL}/v1/savereason/`, {
            method: "POST",
            body: JSON.stringify(saveReasonData),
        });

        const afterDelete = () => {
            window.localStorage.removeItem("jwt");
            showSignedOutToast(props.language);
            closeModalWithAnimation(setIsClosingModalDeleteAccount, setIsOpenModalDeleteAccount);
            setDeleteReasonError(initError);
            setSelected("");
            props.history.push({ pathname: "/" });
        };

        const endpoint =
            props.language === "de"
                ? `${process.env.REACT_APP_API_GOLANG_URL}/v1/deleteaccount/`
                : `${process.env.REACT_APP_API_GOLANG_URL}/v1/deleteaccountEN/`;

        fetch(endpoint, {
            method: "POST",
            body: JSON.stringify(deleteData),
        })
            .then(async (response) => {
                await response.json().catch(() => ({}));
                if (response.ok) afterDelete();
            })
            .catch(() => {});
    };

    const ModalDeleteAccount = ({ setIsOpen, isClosing, setIsClosing }) => {
        return (
            <>
            <div className={`${styles.darkBG} ${isClosing ? styles.darkBGClosing : ""}`} onClick={() => { closeModalWithAnimation(setIsClosing, setIsOpen, () => { setDeleteReasonError(initError); setSelected(""); }); }} />
                <div className={styles.centered}>
                    <div style={{height: '400px', width:'290px', padding:'0', fontFamily:'Roboto', backgroundColor:'#F5F5F6'}} className={`${styles.modal} ${isClosing ? styles.modalClosing : ""}`}>
                        <button type="button" style={{display:'flex', width:'100%', justifyContent:'flex-end', padding:'0px'}} className='none' onClick={() => { closeModalWithAnimation(setIsClosing, setIsOpen, () => { setDeleteReasonError(initError); setSelected(""); }); }}>
                            <i className='icon ms-1 icon-close'></i> 
                        </button>

                        <div style={{padding:'0 10%'}}>
                            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}} className={styles.modalHeader}>
                                <i style={{color:'#4FB8A8', fontSize:'3rem'}} className='icon ms-1 icon-warning'></i> 
                            </div>
                            <div style={{display:'flex', justifyContent: 'center', marginTop: '10px'}}>
                                <h5 style={{fontSize:'14px', padding:'0', marginBottom:'10px', fontWeight:'600'}} className={styles.heading}>
                                    {uiCopy.modal_delete_account_text}
                                </h5>
                            </div>
                        
                        </div>
                        <div style={{padding:'0 10%'}}>
                            <h5 style={{fontSize:'14px', padding:'0', marginBottom:'10px', fontWeight:'600'}} className={styles.heading}>
                                {uiCopy.modal_delete_account_question}
                            </h5>
                            <div style={{fontSize:'14px'}}>
                                <RadioInput 
                                    value={["I don't use the app"]}
                                    selected={selected}
                                    text={uiCopy.modal_delete_account_option_1}
                                    onChange={(val) => {setSelected(val)}}
                                />
                                <RadioInput 
                                    value={["You send to many e-mails"]}
                                    selected={selected}
                                    text={uiCopy.modal_delete_account_option_2}
                                    onChange={(val) => {setSelected(val)}}
                                />
                             
                                <RadioInput 
                                    value={["The app is not usefull"]}
                                    selected={selected}
                                    text={uiCopy.modal_delete_account_option_3}
                                    onChange={(val) => {setSelected(val)}}
                                />
                                <RadioInput 
                                    value={["Something else"]}
                                    selected={selected}
                                    text={uiCopy.modal_delete_account_option_4}
                                    onChange={(val) => {setSelected(val)}}
                                />

                                <div style={{height:'18px'}} className={deleteReasonError.exists ? "text-danger" : "no-danger"}>{deleteReasonError.helperText}</div>
                            </div>
                        </div>
                        <div style={{display:'flex', justifyContent: 'space-around', marginTop:'25px'}} >
                            <button  
                                type="button"
                                style={{width:'unset'}}
                                onClick={() => confirmDelete()}
                                className="snow"
                            >
                                {uiCopy.modal_delete_account_confirm}
                            </button>
                            <button
                                type="button"
                                style={{width:'unset'}}
                                className='ochre'
                                onClick={() => { closeModalWithAnimation(setIsClosing, setIsOpen, () => { setDeleteReasonError(initError); setSelected(""); }); }}
                            >
                                {uiCopy.modal_delete_account_cancel}
                            </button>
                            
                        </div>
                        
                        
                    </div>
                </div>
            </>
        );
    };
    const ModalCancelRecurring = ({ setIsOpen, isClosing, setIsClosing }) => {
        const copy = recurring.dictRecurringCopy();
        return (
            <>
                <div className={`${styles.darkBG} ${isClosing ? styles.darkBGClosing : ""}`} onClick={() => closeModalWithAnimation(setIsClosing, setIsOpen)} />
                <div className={styles.centered}>
                    <div style={{ width: '290px', minHeight: '280px', height: 'auto', padding: '0', fontFamily: 'Roboto', backgroundColor: '#F5F5F6' }} className={`${styles.modal} ${isClosing ? styles.modalClosing : ""}`}>
                        <button
                            type="button"
                            style={{ display: 'flex', width: '100%', justifyContent: 'flex-end', padding: '5px' }}
                            className='none'
                            onClick={() => closeModalWithAnimation(setIsClosing, setIsOpen)}
                        >
                            <i className='icon ms-1 icon-close'></i>
                        </button>

                        <div>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }} className={styles.modalHeader}>
                                <i style={{ color: '#4FB8A8', fontSize: '3rem' }} className='icon ms-1 icon-alert'></i>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px', marginTop: '10px' }}>
                                <h5 style={{ fontFamily: 'Roboto' }} className={styles.heading}>
                                    {copy.confirmTitle}
                                </h5>
                            </div>
                            <div style={{ padding: '0 10%', marginBottom: '18px' }}>
                                <p style={{ fontSize: '13px', color: '#2c3e50', textAlign: 'center', margin: 0, fontFamily: 'Roboto' }}>
                                    {copy.confirmMessage}
                                </p>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '20px', paddingBottom: '20px' }}>
                                <button
                                    type="button"
                                    style={{ width: 'unset' }}
                                    className="snow"
                                    disabled={recurring.recurringCancelBusy}
                                    onClick={() => {
                                        closeModalWithAnimation(setIsClosing, setIsOpen, () => {
                                            void recurring.executeCancel();
                                        });
                                    }}
                                >
                                    {copy.confirmProceed}
                                </button>
                                <button
                                    type="button"
                                    style={{ width: 'unset' }}
                                    className='ochre'
                                    onClick={() => closeModalWithAnimation(setIsClosing, setIsOpen)}
                                >
                                    {copy.confirmAbort}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    const ModalReactivateRecurring = ({ setIsOpen, isClosing, setIsClosing }) => {
        const copy = recurring.dictRecurringCopy();
        return (
            <>
                <div className={`${styles.darkBG} ${isClosing ? styles.darkBGClosing : ""}`} onClick={() => closeModalWithAnimation(setIsClosing, setIsOpen)} />
                <div className={styles.centered}>
                    <div style={{ width: '290px', minHeight: '280px', height: 'auto', padding: '0', fontFamily: 'Roboto', backgroundColor: '#F5F5F6' }} className={`${styles.modal} ${isClosing ? styles.modalClosing : ""}`}>
                        <button
                            type="button"
                            style={{ display: 'flex', width: '100%', justifyContent: 'flex-end', padding: '5px' }}
                            className='none'
                            onClick={() => closeModalWithAnimation(setIsClosing, setIsOpen)}
                        >
                            <i className='icon ms-1 icon-close'></i>
                        </button>

                        <div>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }} className={styles.modalHeader}>
                                <i style={{ color: '#4FB8A8', fontSize: '3rem' }} className='icon ms-1 icon-alert'></i>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px', marginTop: '10px' }}>
                                <h5 style={{ fontFamily: 'Roboto' }} className={styles.heading}>
                                    {copy.confirmReactivateTitle}
                                </h5>
                            </div>
                            <div style={{ padding: '0 10%', marginBottom: '18px' }}>
                                <p style={{ fontSize: '13px', color: '#2c3e50', textAlign: 'center', margin: 0, fontFamily: 'Roboto' }}>
                                    {copy.confirmReactivateMessage}
                                </p>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '20px', paddingBottom: '20px' }}>
                                <button
                                    type="button"
                                    style={{ width: 'unset' }}
                                    className="snow"
                                    disabled={recurring.recurringReactivateBusy}
                                    onClick={() => {
                                        closeModalWithAnimation(setIsClosing, setIsOpen, () => {
                                            void recurring.executeReactivate();
                                        });
                                    }}
                                >
                                    {copy.confirmReactivateProceed}
                                </button>
                                <button
                                    type="button"
                                    style={{ width: 'unset' }}
                                    className='ochre'
                                    onClick={() => closeModalWithAnimation(setIsClosing, setIsOpen)}
                                >
                                    {copy.confirmReactivateAbort}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    const ModalChangePass = ({ setIsOpen, isClosing, setIsClosing }) => {
        return (
            <>
            <div className={`${styles.darkBG} ${isClosing ? styles.darkBGClosing : ""}`} onClick={() => closeModalWithAnimation(setIsClosing, setIsOpen)} />
                <div className={styles.centered}>
                    <div className={`${styles.modal} ${isClosing ? styles.modalClosing : ""}`}>
                        <button type="button" style={{display:'flex', width:'100%', justifyContent:'flex-end', padding:'5px'}} className='none' onClick={() => closeModalWithAnimation(setIsClosing, setIsOpen)}>
                            <i className='icon ms-1 icon-close'></i> 
                        </button>

                        <div>
                        <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}} className={styles.modalHeader}>
                            <i style={{color:'#4FB8A8', fontSize:'3rem'}} className='icon ms-1 icon-alert'></i> 
                        </div>
                        <div style={{display:'flex', justifyContent: 'center', marginBottom: '25px', marginTop: '10px'}}>
                            <h5 style={{fontFamily:'Roboto'}}  className={styles.heading}>
                                {uiCopy.modal_change_password_text}
                            </h5>
                        </div>
                            
                        
                        <div style={{display:'flex', justifyContent: 'space-around'}} >
                            <button  
                                type="button"
                                style={{width:'unset'}}
                                className="snow"
                            >
                                <Link
                                    to={'/changePassword'}
                                    className='link'
                                    style={{textDecoration: 'none'}}
                                    onClick={() => closeModalWithAnimation(setIsClosing, setIsOpen)}
                                >
                                    {uiCopy.modal_change_password_confirm}
                                </Link>
                                
                            </button>
                            <button
                                type="button"
                                style={{width:'unset'}}
                                className='ochre'
                                onClick={() => closeModalWithAnimation(setIsClosing, setIsOpen)}
                            >
                                {uiCopy.modal_change_password_cancel}
                            </button>
                            
                        </div>
                        </div>
                        
                        
                    </div>
                </div>
            </>
        );
    };
    return(
        <>
        <AppHeader 
            currentPathName={props.location.pathname}
            appLanguage={props.language}
            mobile = {mobile}
            comeFrom={comeFrom}
        />
        {isOpenModalChangePass && <ModalChangePass setIsOpen={setIsOpenModalChangePass} isClosing={isClosingModalChangePass} setIsClosing={setIsClosingModalChangePass} />}
        {isOpenModalDeleteAccount && <ModalDeleteAccount setIsOpen={setIsOpenModalDeleteAccount} isClosing={isClosingModalDeleteAccount} setIsClosing={setIsClosingModalDeleteAccount} />}
        {isOpenModalCancelRecurring && <ModalCancelRecurring setIsOpen={setIsOpenModalCancelRecurring} isClosing={isClosingModalCancelRecurring} setIsClosing={setIsClosingModalCancelRecurring} />}
        {isOpenModalReactivateRecurring && <ModalReactivateRecurring setIsOpen={setIsOpenModalReactivateRecurring} isClosing={isClosingModalReactivateRecurring} setIsClosing={setIsClosingModalReactivateRecurring} />}
        <Toast
            toastList={toastProperties}
            position={toastPosition}
            visible={toastVisible}
            language={props.language}
        />

        <section className={classes.upgradeAccountContainer}>
            <div ref={shellRef} className={classes.upgradeAccountShell}>
                <Link
                    className={classes.backLink}
                    to={`/profile/${id}`}
                    aria-label={uiCopy.back_to_profile}
                    style={
                        backLinkLayout
                            ? {
                                  left: mobile ? undefined : backLinkLayout.left,
                                  top: backLinkLayout.top,
                              }
                            : undefined
                    }
                >
                    <i className="icon icon-arrow" aria-hidden />
                </Link>

                <h1 className={classes.pageTitle}>{uiCopy.title}</h1>

                <button
                    type="button"
                    className={classes.linkAccentUpper}
                    onClick={() => setIsOpenModalChangePass(true)}
                >
                    {uiCopy.change_password_link}
                </button>

                <article className={isBookTier ? classes.cardCurrentDark : classes.cardCurrent}>
                    <div className={classes.ribbonCurrent} aria-hidden>
                        {BASIC_CURRENT_CARD[langKey].ribbon}
                    </div>
                    <div className={classes.cardCurrentInner}>
                        <div className={classes.cardCurrentHeading}>
                            <span className={classes.starOutline} aria-hidden>
                                ☆
                            </span>
                            <span>{currentSubscriptionTitle}</span>
                        </div>
                        <div className={classes.featureScrollRegion}>
                            <ul className={classes.featureListOnTeal}>
                                {currentSubscriptionFeatures.map((t, i) =>
                                    featureLiPlain(t, i)
                                )}
                            </ul>
                        </div>
                    </div>
                </article>

                {window.localStorage.getItem('jwt') && recurring.authCheckLoading ? (
                    <div className={classes.recurringCancelWrap} aria-hidden>
                        <div className={classes.recurringAuthSkeleton} />
                    </div>
                ) : null}

                {/* Renovación ya cancelada (API) o mensaje tras cancelar en esta sesión */}
                {isBookTier &&
                !recurring.authCheckLoading &&
                (recurringCancelledLines ||
                    recurring.recurringCancelStatus?.type === 'success') ? (
                    <div className={classes.recurringCancelWrap}>
                        <div className={classes.recurringCancelStatusSuccess}>
                            {recurringCancelledLines ? (
                                <>
                                    <p style={{ margin: 0 }}>{recurringCancelledLines.line1}</p>
                                    {recurringCancelledLines.line2 ? (
                                        <p style={{ margin: '10px 0 0' }}>{recurringCancelledLines.line2}</p>
                                    ) : null}
                                </>
                            ) : (
                                recurring.recurringCancelStatus.message
                            )}
                        </div>
                        {recurringCancelledLines ? (
                            <div className={classes.recurringCancelRow}>
                                <button
                                    type="button"
                                    className={classes.recurringReactivateButton}
                                    disabled={recurring.recurringReactivateBusy}
                                    onClick={() => setIsOpenModalReactivateRecurring(true)}
                                >
                                    {recurring.recurringReactivateBusy
                                        ? recurring.dictRecurringCopy().reactivateProcessing
                                        : recurring.dictRecurringCopy().reactivateRenewal}
                                </button>
                            </div>
                        ) : null}
                        {recurring.recurringReactivateStatus?.type === 'error' ? (
                            <div
                                className={`${classes.recurringCancelStatus} ${classes.recurringCancelStatusError}`}
                            >
                                {recurring.recurringReactivateStatus.message}
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {/* Tras reactivar correctamente (authCheck ya no está en estado “cancelado”) */}
                {isBookTier &&
                !recurring.authCheckLoading &&
                recurring.recurringReactivateStatus?.type === 'success' ? (
                    <div className={classes.recurringCancelWrap}>
                        <div className={classes.recurringCancelStatusSuccess}>
                            {recurring.recurringReactivateStatus.message}
                        </div>
                    </div>
                ) : null}

                {/* Botón cancelar: solo si la renovación sigue activa o no pudimos leer el estado */}
                {isBookTier &&
                !recurring.authCheckLoading &&
                recurring.recurringCancelStatus?.type !== 'success' &&
                !isDictionaryRecurringCancelledState(recurring.authCheck) &&
                (isDictionaryRecurringActive(recurring.authCheck) ||
                    recurring.recurringCancelBusy ||
                    recurring.authCheckFailed) ? (
                    <div className={classes.recurringCancelWrap}>
                        <div className={classes.recurringCancelRow}>
                            <button
                                type="button"
                                className={classes.recurringCancelButton}
                                disabled={recurring.recurringCancelBusy}
                                onClick={() => setIsOpenModalCancelRecurring(true)}
                            >
                                {recurring.recurringCancelBusy
                                    ? recurring.dictRecurringCopy().processing
                                    : recurring.dictRecurringCopy().cancelRenewal}
                            </button>
                        </div>
                        {recurring.recurringCancelStatus?.type === 'error' ? (
                            <div className={`${classes.recurringCancelStatus} ${classes.recurringCancelStatusError}`}>
                                {recurring.recurringCancelStatus.message}
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {isBookTier && hasLinkedBookCode ? (
                    <div className={classes.linkedBookCodeWrap}>
                        <p className={classes.linkedBookCodeLabel}>
                            {uiCopy.linked_book_code_label}
                        </p>
                        <div className={classes.linkedBookCodeRow}>
                            <span className={classes.linkedBookCode}>
                                {linkedBookCode}
                            </span>
                            <button
                                type="button"
                                className={`btn ochre ${classes.unlinkBookCodeBtn}`}
                                disabled={releasingBookCode}
                                onClick={releaseLinkedBookCode}
                            >
                                {releasingBookCode
                                    ? uiCopy.unlink_book_code_loading
                                    : uiCopy.unlink_book_code}
                            </button>
                        </div>
                    </div>
                ) : null}

                {!isBookTier ? (
                    <>
                        <hr className={classes.sectionRule} />

                        <h2 className={classes.sectionSubtitle}>
                            {uiCopy.change_your_subscription}
                        </h2>

                        <article className={classes.cardUpgrade}>
                            <div className={classes.cardUpgradeHeading}>
                                <i className="icon icon-book" aria-hidden />
                                <i className="icon icon-premium" aria-hidden />
                                <span>{uiCopy.book_premium_heading}</span>
                            </div>
                            <div className={classes.featureScrollRegionGreen}>
                                <ul className={classes.featureListOnGreen}>
                                    {bookPremiumFeatures.map((t, i) => featureLiPlain(t, i))}
                                </ul>
                            </div>
                            <div className={classes.upgradeIntro}>
                                {upgradeIntroParagraphs.map((text, i) => (
                                    <p key={`intro-${i}`}>{text}</p>
                                ))}
                            </div>
                            <Link
                                className={classes.btnUpgradeGold}
                                to="/upgradeBookAccount"
                            >
                                {uiCopy.upgrade_account_button}
                            </Link>
                        </article>

                    </>
                ) : null}

                <hr className={classes.sectionRule} />

                <button
                    type="button"
                    className={classes.deleteAccountRow}
                    onClick={() => {
                        setSelected("");
                        setDeleteReasonError(initError);
                        setIsOpenModalDeleteAccount(true);
                    }}
                >
                    <i className="icon-link icon-delete" aria-hidden />
                    <span>
                        {uiCopy.delete_account_link}
                    </span>
                </button>
            </div>
        </section>
        <AppFooter language={props.language} />
        </>
    )
}



